<template>
  <PMain v-model="themeData" :dataKey="key" @drawerChanged="headerDrawerOpen = $event">
    <v-container class="pa-0">
      <!-- START CATEGORY BLOCK -->
      <v-row v-if="themeData.categoryBlock" dense>
        <v-col class="pa-0">
          <div :style="tabWrapperStyle">
            <v-tabs
              v-model="selectedTab"
              :height="50"
              v-bind="themeData.categoryBlock.data.attr"
              :background-color="
                themeData.categoryBlock.data.attr.style.backgroundColor
              "
            >
              <v-tab
                v-for="(category, idx) in categories"
                :key="idx"
                :href="'#category-' + idx"
                @click="scrollToCategory(idx)"
                :active-class="
                  themeData.categoryBlock.data.nodes.tabActive.data.attr.class
                "
              >
                {{ $pt(category.name_translations, category.name) }}
              </v-tab>
              <v-tab
                disabled
                style="min-width: 60px; opacity: 0; pointer-events: none; cursor: default;"
              >
              </v-tab>
            </v-tabs>
            <div
              @click="categoryDrawer = true"
              style="
                position: absolute;
                right: 0;
                top: 0;
                height: 50px;
                width: 50px;
                z-index: 999;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                border-left: 1px solid rgba(0,0,0,0.12);
              "
              :style="{ backgroundColor: listBtnBgColor }"
            >
              <v-icon :color="listBtnIconColor">mdi-format-list-bulleted</v-icon>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="themeData.categoryBlock && themeData.categoryBlock.data.fixed"
        style="height: 50px"
      >
        <v-col>
          <br />
        </v-col>
      </v-row>
      <!-- END CATEGORY BLOCK -->

      <v-row v-if="isProductLoading">
        <v-col v-for="i in 5" cols="12" :key="i">
          <v-skeleton-loader type="card" height="150"></v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row v-else>
        <template v-for="(group, groupIdx) in groupedProducts">
          <!-- Category Header -->
          <v-col
            cols="12"
            :key="'header-' + groupIdx"
            :ref="'category-section-' + groupIdx"
            class="pa-0"
          >
            <div
              class="category-header d-flex align-center justify-space-between"
              :style="{
                paddingLeft: cardMarginParts.left,
                paddingRight: cardMarginParts.right,
              }"
            >
              <span
                v-if="themeData.productListBlock"
                :style="{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: themeData.productListBlock.data.attr.style
                    ? themeData.productListBlock.data.attr.style.color
                    : '#000',
                }"
              >
                {{ categoryName(group.category) }}
              </span>
              <span class="category-count">
                {{ group.product_count }}
                {{ group.product_count === 1 ? $t('menu.product') : $t('menu.products') }}
              </span>
            </div>
          </v-col>

          <!-- Products in this category -->
          <v-col
            v-for="(product, idx) in group.products"
            :key="'product-' + groupIdx + '-' + idx"
            cols="12"
            class="py-1"
            :style="{
              paddingLeft: cardMarginParts.left,
              paddingRight: cardMarginParts.right,
            }"
          >
            <v-card
              v-if="themeData.productListBlock"
              v-bind="themeData.productListBlock.data.attr"
              :style="{ overflow: 'hidden' }"
              :class="{ 'product-unavailable': !isProductAvailable(product) }"
            >
              <v-card-text class="pa-3">
                <div class="d-flex" style="align-items: flex-start;">
                  <!-- Left: Name, Description, Allergens -->
                  <div class="flex-grow-1 pr-2" style="min-width: 0;">
                    <!-- Product Name -->
                    <div
                      v-bind="
                        themeData.productListBlock.data.nodes.title.data.attr
                      "
                    >
                      {{
                        $pt(
                          product.menu_product.product.name_translations,
                          product.menu_product.product.name
                        )
                      }}
                    </div>

                    <!-- Description -->
                    <div
                      v-if="
                        themeData.productListBlock.data.nodes.description &&
                        themeData.productListBlock.data.nodes.description.data
                          .active
                      "
                      :class="[themeData.productListBlock.data.nodes.description.data.attr.class, 'mt-1']"
                      :style="Object.assign({},
                        themeData.productListBlock.data.nodes.description.data.attr.style,
                        descriptionClampStyle
                      )"
                    >
                      {{
                        $pt(
                          product.menu_product.product.description_translations,
                          product.menu_product.product.description_2
                        )
                      }}
                    </div>

                    <!-- Allergens -->
                    <div
                      v-if="
                        productAllergen.active &&
                        product.menu_product &&
                        product.menu_product.product &&
                        product.menu_product.product.allergens &&
                        product.menu_product.product.allergens.length > 0
                      "
                      class="mt-1"
                    >
                      <div
                        v-if="productAllergen.type == 'icon'"
                        class="d-flex flex-wrap"
                      >
                        <div
                          v-for="(allergen, aIdx) in product.menu_product.product
                            .allergens"
                          :key="aIdx"
                          small
                          v-bind="productAllergen.attr"
                        >
                          <img
                            :src="`/asset/allergen/1/${allergen.code}.png`"
                            :alt="$allergenName(allergen)"
                            width="24"
                            height="24"
                          />
                        </div>
                      </div>
                      <v-chip-group v-else>
                        <v-chip
                          v-for="(allergen, aIdx) in product.menu_product.product
                            .allergens"
                          :key="aIdx"
                          small
                          v-bind="productAllergen.attr"
                        >
                          {{ $allergenName(allergen) }}
                        </v-chip>
                      </v-chip-group>
                    </div>
                  </div>

                  <!-- Right: Image + Price + Button stacked vertically -->
                  <div class="flex-shrink-0 d-flex flex-column align-end" style="width: 100px;">
                    <!-- Image -->
                    <v-img
                      v-if="
                        imageBlockData.showImage &&
                        product.menu_product &&
                        product.menu_product.product &&
                        $productImage(product.menu_product.product)
                      "
                      :src="$productImage(product.menu_product.product)"
                      :lazy-src="$productImage(product.menu_product.product)"
                      width="100"
                      height="100"
                      style="border-radius: 8px;"
                    >
                      <template v-slot:placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular
                            indeterminate
                            color="grey lighten-5"
                          ></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>

                    <!-- Price + Add button row -->
                    <div class="d-flex align-center mt-1" style="width: 100%;">
                      <div
                        :style="{
                          color: themeData.productListBlock.data.nodes.price.data.attr.style.color,
                          fontFamily: themeData.productListBlock.data.nodes.price.data.attr.style.fontFamily,
                          fontSize: themeData.productListBlock.data.nodes.price.data.attr.style.fontSize,
                          lineHeight: themeData.productListBlock.data.nodes.price.data.attr.style.lineHeight,
                          textAlign: 'left',
                          margin: '0px',
                          padding: '0px',
                        }"
                      >
                        <span style="margin-right: 5px">
                          {{
                            $store.getters["theme/getIsCurrencyShownAtProduct"]
                              ? $store.getters["theme/getCurrencySymbol"]
                              : ""
                          }}
                        </span>
                        {{ product.price | formatPrice }}
                      </div>
                      <v-spacer />
                      <!-- Out of stock wins over a schedule: it is the
                           stronger statement and keeps the existing image. -->
                      <div v-if="!getItemShopStock(product)">
                        <v-img src="/asset/out-of-stock.png" width="48" />
                      </div>
                      <div v-else-if="isProductAvailable(product)">
                        <div
                          v-if="!$store.getters['cart/isAllRoundFinished']"
                        >
                          <PButton
                            v-if="
                              themeData.productListBlock.data.nodes.plusIcon
                                .data.active
                            "
                            v-model="
                              themeData.productListBlock.data.nodes.plusIcon
                                .data
                            "
                            @click="handleAddToCart(product)"
                          />
                        </div>
                      </div>
                    </div>
                    <!-- Its own line under the price: the column is only
                         100px wide, too narrow to share a line with it. -->
                    <div
                      v-if="getItemShopStock(product) && !isProductAvailable(product)"
                      class="availability-label mt-1"
                    >
                      {{ productAvailabilityLabel(product) }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </template>
        <v-col cols="12" style="height: 80px;" />
      </v-row>

      <ProductDetail
        v-model="productId"
        :show="productDialog"
        @closed="productDialog = false"
        :currentPageAttr="themeData"
      />

      <v-navigation-drawer
        v-model="categoryDrawer"
        fixed
        right
        temporary
        width="280"
      >
        <v-list-item class="py-3">
          <v-list-item-title class="text-h6">{{ $t('menu.categories') }}</v-list-item-title>
          <v-btn icon @click="categoryDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-list-item>
        <v-divider />
        <v-list>
          <v-list-item
            v-for="(group, idx) in groupedProducts"
            :key="idx"
            @click="scrollToCategory(idx); categoryDrawer = false"
            dense
          >
            <v-list-item-avatar tile size="48" v-if="group.category.image">
              <v-img :src="group.category.image.startsWith('http') ? group.category.image : apiServer + group.category.image" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ categoryName(group.category) }}</v-list-item-title>
              <v-list-item-subtitle>{{ group.product_count }} {{ group.product_count === 1 ? $t('menu.product') : $t('menu.products') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>
    </v-container>
  </PMain>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import ProductDetail from "./ProductDetail.vue";
import PMain from "../components/PMain.vue";
import PButton from "../components/_render/PButton.vue";
import {
  isAvailable,
  availabilityLabel,
  earliestNextAvailableAt,
} from "../helpers/availability";

// Smart Menu windows open and close while a guest has the menu open.
const REFRESH_INTERVAL_MS = 90 * 1000;
// Past the opening, so POSHUB's clock has certainly crossed it too.
const BOUNDARY_GRACE_MS = 10 * 1000;
// setTimeout fires immediately for anything above a signed 32-bit int.
const MAX_TIMEOUT_MS = 2147483647;

export default {
  data: () => ({
    key: "menu",
    productDialog: false,
    productId: null,
    themeData: {},
    selectedTab: "category-0",
    isScrolling: false,
    categoryDrawer: false,
    headerDrawerOpen: false,
    refreshInterval: null,
    boundaryTimeout: null,
  }),
  components: {
    ProductDetail,
    PMain,
    PButton,
  },
  filters: {
    formatPrice(value) {
      return Number(value).toFixed(2);
    },
  },
  methods: {
    /**
     * `group.category` comes from the grouped product payload, where it is a
     * raw nested model with no `name_translations`. The same category from
     * /qr-app/category does carry the map, and both are loaded on the same
     * mount, so resolve by id. A category missing from that list renders its
     * nested base text rather than nothing.
     */
    categoryName(nestedCategory) {
      if (!nestedCategory) {
        return "";
      }
      const withTranslations = this.categoryById[nestedCategory.id];
      return this.$pt(
        withTranslations && withTranslations.name_translations,
        nestedCategory.name
      );
    },

    /**
     * defaultMenuId comes from the theme, which PMain fetches asynchronously, so
     * on a cold load it is still undefined when mounted() runs. Fetching anyway
     * produced `category?menu_id=undefined` and a product request with no menu
     * filter -- a wasted round trip each, and the unfiltered product call is the
     * slow one (~1.5 s). It also raced the real fetch: the watcher's category
     * call cancelled the mount's, which is what rejected inside mounted().
     *
     * Skipping is safe because the watcher fires the moment the theme lands and
     * fetches with the real id. This deliberately does NOT cache: the menu is
     * still refetched on every mount and whenever the menu id changes, so a
     * sold-out or withdrawn product never lingers.
     */
    async loadMenu() {
      if (!this.defaultMenuId) {
        return;
      }
      await this.getCategoryAll({ menu_id: this.defaultMenuId });
      await this.getAllGrouped({ menu_id: this.defaultMenuId });
    },

    /**
     * Background refresh. Skipped while the tab is hidden, the product dialog
     * is open (it holds the row it was opened with) or the category drawer is
     * open; the next tick or the return to the tab catches up. The scroll
     * position and the highlighted category are put back afterwards, and the
     * scroll handler is held off meanwhile so it does not re-pick the tab
     * from a half-rendered list.
     */
    async refreshMenu() {
      if (!this.defaultMenuId || document.hidden || this.productDialog || this.categoryDrawer) {
        return;
      }
      const scrollTop = window.pageYOffset
        || (document.documentElement && document.documentElement.scrollTop)
        || 0;
      const tab = this.selectedTab;
      this.isScrolling = true;
      try {
        await this.refreshGrouped({ menu_id: this.defaultMenuId });
        await this.$nextTick();
        window.scrollTo(0, scrollTop);
        this.selectedTab = tab;
      } finally {
        setTimeout(() => {
          this.isScrolling = false;
        }, 400);
      }
    },

    onVisibilityChange() {
      if (!document.hidden) {
        this.refreshMenu();
      }
    },

    // One timer, re-armed whenever the menu changes, for shortly after the
    // earliest opening still ahead in it.
    scheduleBoundaryRefresh() {
      clearTimeout(this.boundaryTimeout);
      this.boundaryTimeout = null;
      const earliest = earliestNextAvailableAt(this.groupedProducts);
      if (!earliest) {
        return;
      }
      const delay = Math.min(
        Math.max(earliest.diff(Date.now()), 0) + BOUNDARY_GRACE_MS,
        MAX_TIMEOUT_MS
      );
      this.boundaryTimeout = setTimeout(() => this.refreshMenu(), delay);
    },

    scrollToCategory(idx) {
      this.isScrolling = true;
      const ref = this.$refs["category-section-" + idx];
      if (ref && ref[0]) {
        this.$vuetify.goTo(ref[0], {
          offset: (this.themeData.categoryBlock && this.themeData.categoryBlock.data && this.themeData.categoryBlock.data.fixed) ? 110 : 60,
          duration: 300,
        });
      }
      setTimeout(() => {
        this.isScrolling = false;
      }, 400);
    },

    handleScroll: _.throttle(function () {
      if (this.isScrolling) return;
      // At the top of the page the first category is by definition the one on
      // screen, so say that outright rather than deriving it. That also guards
      // the walk below: until layout settles the sections can all report
      // roughly the same rect.top, and a backwards walk then matches the LAST
      // of them -- which is how an unscrolled menu ended up highlighting
      // Desserts.
      const scrollTop = window.pageYOffset
        || (document.documentElement && document.documentElement.scrollTop)
        || 0;
      if (scrollTop <= 0) {
        this.selectedTab = "category-0";
        return;
      }
      const offset = (this.themeData.categoryBlock && this.themeData.categoryBlock.data && this.themeData.categoryBlock.data.fixed) ? 120 : 70;
      for (let i = this.groupedProducts.length - 1; i >= 0; i--) {
        const ref = this.$refs["category-section-" + i];
        if (ref && ref[0]) {
          const rect = ref[0].getBoundingClientRect();
          if (rect.top <= offset) {
            this.selectedTab = "category-" + i;
            return;
          }
        }
      }
    }, 100),

    ...mapActions({
      getCategoryAll: "category/getAll",
      getAllGrouped: "product/getAllGrouped",
      refreshGrouped: "product/refreshGrouped",
    }),
    applyTabContainer(newValue) {
      document.body.style.setProperty(
        "--tabContainerBackgroundColor",
        newValue.categoryBlock.data.attr.style.backgroundColor
      );
      document.body.style.setProperty(
        "--tabContainerBorderRadius",
        newValue.categoryBlock.data.attr.style.borderRadius
      );
      document.body.style.setProperty(
        "--tabContainerPadding",
        newValue.categoryBlock.data.attr.style.padding
      );
      document.body.style.setProperty(
        "--tabContainerMargin",
        newValue.categoryBlock.data.attr.style.margin
      );
      document.body.style.setProperty(
        "--tabContainerColor",
        newValue.categoryBlock.data.attr.style.color
      );
    },
    applyTab(newValue) {
      document.body.style.setProperty(
        "--tabBackgroundColor",
        newValue.categoryBlock.data.nodes.tab.data.attr.style.backgroundColor
      );

      document.body.style.setProperty(
        "--tabBorderRadius",
        newValue.categoryBlock.data.nodes.tab.data.attr.style.borderRadius
      );

      document.body.style.setProperty(
        "--tabPadding",
        newValue.categoryBlock.data.nodes.tab.data.attr.style.padding
      );

      document.body.style.setProperty(
        "--tabMargin",
        newValue.categoryBlock.data.nodes.tab.data.attr.style.margin
      );

      document.body.style.setProperty(
        "--tabColor",
        newValue.categoryBlock.data.nodes.tab.data.attr.style.color
      );

      document.body.style.setProperty(
        "--tabFontSize",
        newValue.categoryBlock.data.nodes.tab.data.attr.style.fontSize
      );
    },
    applyTabActive(newValue) {
      if (!newValue.categoryBlock.data.nodes.tabActive.data.underline) {
        document.body.style.setProperty("--tabActiveUnderline", "0px");
      } else {
        document.body.style.setProperty(
          "--tabActiveUnderline",
          `${newValue.categoryBlock.data.nodes.tabActive.data.underlineSize}px`
        );
        document.body.style.setProperty(
          "--tabActiveUnderlineColor",
          newValue.categoryBlock.data.nodes.tabActive.data.underlineColor
        );
      }

      document.body.style.setProperty(
        "--tabActiveBackgroundColor",
        newValue.categoryBlock.data.nodes.tabActive.data.attr.style
          .backgroundColor
      );

      document.body.style.setProperty(
        "--tabActiveBorderRadius",
        newValue.categoryBlock.data.nodes.tabActive.data.attr.style.borderRadius
      );

      document.body.style.setProperty(
        "--tabActivePadding",
        newValue.categoryBlock.data.nodes.tabActive.data.attr.style.padding
      );

      document.body.style.setProperty(
        "--tabActiveMargin",
        newValue.categoryBlock.data.nodes.tabActive.data.attr.style.margin
      );

      document.body.style.setProperty(
        "--tabActiveColor",
        newValue.categoryBlock.data.nodes.tabActive.data.attr.style.color
      );
      document.body.style.setProperty(
        "--tabActiveFontSize",
        newValue.categoryBlock.data.nodes.tabActive.data.attr.style.fontSize
      );
    },
    handleAddToCart(productId) {
      this.productDialog = true;
      this.productId = productId;
    },
    pick(object, key) {
      return _.pick(object, key);
    },
    isProductAvailable(product) {
      return isAvailable(product);
    },
    productAvailabilityLabel(product) {
      return availabilityLabel(product, (key, values) => this.$t(key, values), this.$i18n.locale);
    },
    getItemShopStock(item) {
      let stock = false;
      if (item.menu_product) {
        let stockShops = JSON.parse(item.menu_product.product.status);
        stockShops.map((stockStatus) => {
          if (stockStatus.stock_status == process.env.VUE_APP_SHOP_ID + "-1") {
            stock = true;
          }
        });
      }
      return stock;
    },
  },

  watch: {
    themeData: {
      handler(newValue) {
        this.applyTabContainer(newValue);
        this.applyTab(newValue);
        this.applyTabActive(newValue);
      },
      deep: true,
    },

    async defaultMenuId(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.loadMenu();
      }
    },

    groupedProducts() {
      this.scheduleBoundaryRefresh();
    },
  },
  async mounted() {
    // Attached BEFORE the fetches. mounted() is async, so a rejected await used
    // to abort the hook right here and this listener was never added -- which
    // left the category tab strip frozen on the first tab for the whole visit.
    // The refresh timer and visibility listener go in for the same reason.
    window.addEventListener("scroll", this.handleScroll);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.refreshInterval = setInterval(() => this.refreshMenu(), REFRESH_INTERVAL_MS);
    await this.loadMenu();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    clearInterval(this.refreshInterval);
    clearTimeout(this.boundaryTimeout);
  },
  computed: {
    apiServer() {
      return process.env.VUE_APP_ROOT_API_SERVER || '';
    },
    ...mapState({
      groupedProducts: (state) => state.product.groupedData,
      isProductLoading: (state) => state.product.isLoading,
      categories: (state) => state.category.data,
      isCategoryLoading: (state) => state.category.isLoading,
    }),

    // Index for categoryName(). Rebuilt only when state.category.data changes.
    categoryById() {
      return this.categories.reduce((acc, category) => {
        acc[category.id] = category;
        return acc;
      }, {});
    },

    cardMarginParts() {
      if (this.themeData.productListBlock &&
          this.themeData.productListBlock.data &&
          this.themeData.productListBlock.data.attr &&
          this.themeData.productListBlock.data.attr.style &&
          this.themeData.productListBlock.data.attr.style.margin) {
        var parts = this.themeData.productListBlock.data.attr.style.margin.split(' ');
        if (parts.length >= 4) {
          return { left: parts[3], right: parts[1] };
        }
      }
      return { left: '16px', right: '16px' };
    },
    descriptionClampStyle() {
      var lines = this.themeData && this.themeData.productListBlock &&
        this.themeData.productListBlock.data && this.themeData.productListBlock.data.nodes &&
        this.themeData.productListBlock.data.nodes.description &&
        this.themeData.productListBlock.data.nodes.description.data &&
        this.themeData.productListBlock.data.nodes.description.data.lines;
      var count = lines || 2;
      if (count > 0) {
        return {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          '-webkit-line-clamp': count,
          '-webkit-box-orient': 'vertical',
        };
      }
      return {};
    },
    tabWrapperStyle() {
      const isFixed = this.themeData.categoryBlock.data.fixed;
      const marginTop = (this.themeData && this.themeData.categoryBlock && this.themeData.categoryBlock.data && this.themeData.categoryBlock.data.attr && this.themeData.categoryBlock.data.attr.style && this.themeData.categoryBlock.data.attr.style.marginTop) || '0px';
      const bgColor = (this.themeData && this.themeData.categoryBlock && this.themeData.categoryBlock.data && this.themeData.categoryBlock.data.attr && this.themeData.categoryBlock.data.attr.style && this.themeData.categoryBlock.data.attr.style.backgroundColor) || '#ffffff';
      let css = {
        position: isFixed ? 'fixed' : 'relative',
        zIndex: (this.categoryDrawer || this.headerDrawerOpen) ? 0 : (isFixed ? 10 : 1),
        left: 0,
        right: 0,
        backgroundColor: bgColor,
      };
      if (isFixed) {
        css.top = 'calc(56px + ' + marginTop + ')';
      }
      return css;
    },

    listBtnBgColor() {
      var style = this.themeData && this.themeData.categoryBlock && this.themeData.categoryBlock.data && this.themeData.categoryBlock.data.attr && this.themeData.categoryBlock.data.attr.style;
      return (style && style.backgroundColor) || '#ffffff';
    },
    listBtnIconColor() {
      var style = this.themeData && this.themeData.categoryBlock && this.themeData.categoryBlock.data && this.themeData.categoryBlock.data.nodes && this.themeData.categoryBlock.data.nodes.tab && this.themeData.categoryBlock.data.nodes.tab.data && this.themeData.categoryBlock.data.nodes.tab.data.attr && this.themeData.categoryBlock.data.nodes.tab.data.attr.style;
      return (style && style.color) || '#000000';
    },

    imageBlockData() {
      if (this.themeData.productListBlock) {
        return this.themeData.productListBlock.data.nodes.image.data;
      }
      return {};
    },
    titleBlockData() {
      if (this.themeData.productListBlock) {
        return this.themeData.productListBlock.data.nodes.title.data;
      }
      return {};
    },

    productAllergen() {
      if (this.themeData.productListBlock) {
        return this.themeData.productListBlock.data.nodes.allergen.data;
      }
      return {};
    },

    categoryId() {
      const { params } = this.$route;
      return params.category_id;
    },

    defaultMenuId() {
      const themeMenu = this.$store.getters["theme/menu"];
      if (themeMenu && themeMenu.categoryBlock) {
        const { menuId } = themeMenu.categoryBlock.data;
        return menuId;
      }
      return;
    },

    getInitialCategoryId() {
      if (this.categoryId) {
        return this.categoryId;
      }
      if (this.categories.length > 0) {
        return this.categories[0].id;
      }
      return null;
    },
  },
};
</script>

<style>
.v-slide-group__next,
.v-slide-group__prev,
.v-tab {
  background: var(--tabBackgroundColor, #1b1b1b);
  border-radius: var(--tabBorderRadius, 50px);
  color: var(--tabColor, #ffffff) !important;
  padding: var(--tabPadding, 0px 0px 0px 0px) !important;
  margin: var(--tabMargin, 0px 0px 0px 0px) !important;
  font-size: var(--tabFontSize, 16px) !important;
}
.v-tab--active {
  background: var(--tabActiveBackgroundColor, #1b1b1b);
  border-radius: var(--tabActiveBorderRadius, 50px);
  color: var(--tabActiveColor, #ffffff) !important;
  padding: var(--tabActivePadding, 0px 0px 0px 0px) !important;
  margin: var(--tabActiveMargin, 0px 0px 0px 0px) !important;
  font-size: var(--tabActiveFontSize, 16px) !important;
  border-bottom: var(--tabActiveUnderline, 4px) solid
    var(--tabActiveUnderlineColor, #000000) !important;
}
.v-tabs {
  left: 0;
}
.v-tabs-slider-wrapper {
  display: none !important;
  border: 0px !important;
}

.v-slide-group__prev,
.v-slide-group__next {
  display: none !important;
}

.category-header {
  padding: 12px 0px 12px 0px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 0px;
}

.category-count {
  font-size: 13px;
  opacity: 0.5;
}

/* Smart Menu: outside its schedule, shown but not orderable. */
.product-unavailable .v-image,
.product-unavailable .v-card__text > .d-flex > div:first-child {
  opacity: 0.45;
  filter: grayscale(100%);
}
</style>
